import React, { useState } from 'react';
import { db, storage } from '../firebaseConfig';
import { collection, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { showToast } from '../components/Toast';
import '../css/biography-modal.css';

function BiographyModal({ onClose, biography = null, onSuccess }) {
  const [formData, setFormData] = useState({
    title: biography?.title || '',
    description: biography?.description || '',
    content: biography?.content || '',
    imageUrl: biography?.imageUrl || ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(biography?.imageUrl || null);
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        showToast('❌ Please select a valid image', 'error', true);
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        showToast('❌ The image must be less than 5MB', 'error', true);
        return;
      }

      setImageFile(file);
      
      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async (file) => {
    try {
      const timestamp = Date.now();
      const imageName = `biography_${timestamp}_${file.name}`;
      const imageRef = ref(storage, `biography/${imageName}`);

      console.log('📤 Subiendo imagen a Firebase Storage...');
      const snapshot = await uploadBytes(imageRef, file);
      
      const downloadUrl = await getDownloadURL(snapshot.ref);
      console.log('✅ Imagen subida correctamente');
      
      return downloadUrl;
    } catch (error) {
      console.error('❌ Error al subir imagen:', error);
      
      if (error.code === 'storage/unauthorized') {
        showToast('❌ You do not have permission to upload images', 'error', true);
      } else if (error.code === 'storage/retry-limit-exceeded') {
        showToast('❌ Connection error. Try again', 'error', true);
      } else {
        showToast('❌ Error uploading image: ' + error.message, 'error', true);
      }
      throw error;
    }
  };

  const deleteImageFromStorage = async (imageUrl) => {
    try {
      if (!imageUrl) return;
      
      const pathStart = imageUrl.indexOf('/o/') + 3;
      const pathEnd = imageUrl.indexOf('?alt=media');
      const imagePath = decodeURIComponent(imageUrl.substring(pathStart, pathEnd));
      
      const imageRef = ref(storage, imagePath);
      await deleteObject(imageRef);
      console.log('✅ Imagen eliminada de Storage');
    } catch (error) {
      console.error('⚠️ Error al eliminar imagen de Storage:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!formData.title.trim()) {
        showToast('❌ Title is required', 'error', true);
        setLoading(false);
        return;
      }

      if (!formData.description.trim()) {
        showToast('❌ Description is required', 'error', true);
        setLoading(false);
        return;
      }

      let imageUrl = formData.imageUrl;

      if (imageFile) {
        if (biography?.imageUrl) {
          await deleteImageFromStorage(biography.imageUrl);
        }

        imageUrl = await uploadImage(imageFile);
      }

      if (biography?.id) {
        const docRef = doc(db, 'biography', biography.id);
        await updateDoc(docRef, {
          title: formData.title,
          description: formData.description,
          content: formData.content,
          imageUrl: imageUrl,
          updatedAt: serverTimestamp()
        });
        showToast('✅ Contenido actualizado exitosamente', 'success', true);
      } else {
        await addDoc(collection(db, 'biography'), {
          title: formData.title,
          description: formData.description,
          content: formData.content,
          imageUrl: imageUrl,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        showToast('✅ Contenido creado exitosamente', 'success', true);
      }

      setLoading(false);
      if (onSuccess) onSuccess();
      setTimeout(() => onClose(), 500);
    } catch (error) {
      setLoading(false);
      console.error('Error:', error);

      if (error.code === 'permission-denied') {
        showToast('❌ You do not have permission to perform this action', 'error', true);
      } else {
        showToast('❌ Error: ' + error.message, 'error', true);
      }
    }
  };

  const handleDelete = async () => {
    if (!biography?.id) return;

    setLoading(true);
    try {
      if (biography.imageUrl) {
        await deleteImageFromStorage(biography.imageUrl);
      }

      await deleteDoc(doc(db, 'biography', biography.id));
      showToast('✅ Contenido eliminado exitosamente', 'success', true);
      setLoading(false);
      if (onSuccess) onSuccess();
      setTimeout(() => onClose(), 500);
    } catch (error) {
      setLoading(false);
      console.error('Error:', error);

      if (error.code === 'permission-denied') {
        showToast('❌ You do not have permission to delete this content', 'error', true);
      } else {
        showToast('❌ Error deleting: ' + error.message, 'error', true);
      }
    }
  };

  return (
    <div className="biography-modal-overlay" onClick={onClose}>
      <div className="biography-modal-container" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>

        {showDeleteConfirm ? (
          <div className="delete-confirm">
            <h3>⚠️ Confirmar eliminación</h3>
            <p>¿Estás seguro de que deseas eliminar este contenido? Esta acción no se puede deshacer.</p>
            <div className="delete-buttons">
              <button 
                className="btn-cancel" 
                onClick={() => setShowDeleteConfirm(false)}
                disabled={loading}
              >
                Cancelar
              </button>
              <button 
                className="btn-delete" 
                onClick={handleDelete}
                disabled={loading}
              >
                {loading ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="modal-header-compact">
              <h2>{biography?.id ? '✏️ Editar' : '➕ Agregar'}</h2>
            </div>

            <form onSubmit={handleSubmit} className="biography-form-compact">
              {/* Lado izquierdo - Preview e Imagen */}
              <div className="form-left">
                {imagePreview && (
                  <div className="image-preview-compact">
                    <img src={imagePreview} alt="Vista previa" />
                  </div>
                )}
                
                <div className="form-group-compact">
                  <label htmlFor="imageFile">🖼️ Imagen</label>
                  <div className="image-upload-wrapper-compact">
                    <input
                      type="file"
                      id="imageFile"
                      accept="image/*"
                      onChange={handleImageChange}
                      disabled={loading}
                      className="image-input"
                    />
                    <label htmlFor="imageFile" className="image-upload-label-compact">
                      {imageFile ? '✓ ' + imageFile.name.substring(0, 15) : '📁 Seleccionar'}
                    </label>
                  </div>
                </div>

                {imagePreview && (
                  <button
                    type="button"
                    className="btn-remove-image-compact"
                    onClick={() => {
                      setImageFile(null);
                      setImagePreview(null);
                      setFormData(prev => ({...prev, imageUrl: ''}));
                    }}
                    disabled={loading}
                  >
                    ✕ Remover
                  </button>
                )}
              </div>

              {/* Lado derecho - Campos de texto */}
              <div className="form-right">
                <div className="form-group-compact">
                  <label htmlFor="title">📝 Título</label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Título"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-group-compact">
                  <label htmlFor="description">📄 Descripción</label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Descripción breve"
                    rows="3"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-group-compact">
                  <label htmlFor="content">📖 Contenido</label>
                  <textarea
                    id="content"
                    name="content"
                    value={formData.content}
                    onChange={handleChange}
                    placeholder="Contenido (opcional)"
                    rows="4"
                    disabled={loading}
                  />
                </div>

                {/* Botones */}
                <div className="form-buttons-compact">
                  <button 
                    type="button" 
                    className="btn-cancel-compact" 
                    onClick={onClose}
                    disabled={loading}
                  >
                    Cancelar
                  </button>
                  {biography?.id && (
                    <button 
                      type="button" 
                      className="btn-delete-compact" 
                      onClick={() => setShowDeleteConfirm(true)}
                      disabled={loading}
                    >
                      🗑️
                    </button>
                  )}
                  <button 
                    type="submit" 
                    className="btn-submit-compact" 
                    disabled={loading}
                  >
                    {loading ? '...' : '✓'}
                  </button>
                </div>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default BiographyModal;